import { Link } from "react-router-dom";
import { ArrowUpRight, PlayCircle, MapPin, Clock, Monitor, Sparkles, Target, Users, Award, Rocket, ShieldCheck, LineChart, GraduationCap, Server, Code2, Brain } from "lucide-react";
import Reveal from "@/components/Reveal";
import ApplicationForm from "@/components/ApplicationForm";
import { PartnersMarquee, RecognitionsStrip } from "@/components/PartnersMarquee";
import { INTERNSHIPS } from "@/data/internships";
import { SERVICES, STEPS, STATS_HERO, STATS_ABOUT, WHY_US, CERT_BENEFITS, TESTIMONIALS, FAQ, STATS_IMPACT } from "@/data/content";
import { useState, useEffect } from "react";

const SERVICE_ICONS = [Code2, Brain, GraduationCap, Server, LineChart, ShieldCheck];
const SERVICE_ACCENTS = [
  { bg: "bg-indigo-50", text: "text-indigo-700", border: "border-indigo-100", hoverBg: "group-hover:bg-indigo-700", hoverText: "group-hover:text-white", hoverBorder: "group-hover:border-indigo-700" },
  { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-100", hoverBg: "group-hover:bg-amber-600", hoverText: "group-hover:text-white", hoverBorder: "group-hover:border-amber-600" },
  { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-100", hoverBg: "group-hover:bg-emerald-700", hoverText: "group-hover:text-white", hoverBorder: "group-hover:border-emerald-700" },
  { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-100", hoverBg: "group-hover:bg-rose-700", hoverText: "group-hover:text-white", hoverBorder: "group-hover:border-rose-700" },
  { bg: "bg-sky-50", text: "text-sky-700", border: "border-sky-100", hoverBg: "group-hover:bg-sky-700", hoverText: "group-hover:text-white", hoverBorder: "group-hover:border-sky-700" },
  { bg: "bg-neutral-100", text: "text-neutral-900", border: "border-neutral-200", hoverBg: "group-hover:bg-neutral-950", hoverText: "group-hover:text-white", hoverBorder: "group-hover:border-neutral-950" },
];

export default function Home() {
  return (
    <div className="pt-16 md:pt-20">
      <Hero />
      <RecognitionsBar />
      <AboutIntro />
      <PopularInternships />
      <HowItWorks />
      <ServicesGrid />
      <SamAiHighlight />
      <ImpactStrip />
      <WhyChoose />
      <Certification />
      <Testimonials />
      <TrustedPartners />
      <FaqSection />
      <ApplySection />
    </div>
  );
}

/* ---------------- HERO ---------------- */
function Hero() {
  return (
    <section id="hero" className="relative overflow-hidden">
      {/* Soft colored blobs for premium depth */}
      <div className="pointer-events-none absolute -top-32 -left-20 w-[520px] h-[520px] rounded-full blur-3xl opacity-40" style={{ background: "radial-gradient(circle, rgba(251,146,60,0.35) 0%, transparent 70%)" }} />
      <div className="pointer-events-none absolute top-40 right-0 w-[520px] h-[520px] rounded-full blur-3xl opacity-40" style={{ background: "radial-gradient(circle, rgba(99,102,241,0.35) 0%, transparent 70%)" }} />

      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-16 md:pb-24 relative">
        <Reveal>
          <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-6 mb-8">
            <div className="flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-100 pl-2 pr-4 py-1.5">
                <span className="relative inline-flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
                </span>
                <span className="text-xs uppercase tracking-[0.22em] text-emerald-700 font-mono">
                  Internship intake open
                </span>
              </span>
              <span className="hidden sm:inline-flex items-center gap-2 rounded-full bg-white border border-neutral-200 px-4 py-1.5 text-xs uppercase tracking-[0.22em] text-neutral-600 font-mono">
                Cohort · Bangalore
              </span>
              <span className="hidden md:inline-flex items-center gap-2 rounded-full bg-white border border-neutral-200 px-4 py-1.5 text-xs uppercase tracking-[0.22em] text-neutral-600 font-mono">
                MSME · Startup India
              </span>
            </div>

            <div className="flex relative bg-gradient-to-br from-emerald-50/90 to-emerald-50/40 border border-emerald-400 rounded-xl p-4 pr-16 shadow-sm backdrop-blur-sm max-w-sm w-full sm:w-fit">
              <div className="absolute top-0 right-4 flex flex-col drop-shadow-sm">
                <div className="w-8 h-9 bg-emerald-500 flex items-center justify-center">
                  <Award className="w-4 h-4 text-white" />
                </div>
                <div className="flex">
                  <div className="w-0 h-0 border-l-[16px] border-l-emerald-500 border-b-[12px] border-b-transparent"></div>
                  <div className="w-0 h-0 border-r-[16px] border-r-emerald-500 border-b-[12px] border-b-transparent"></div>
                </div>
              </div>

              <div className="flex items-start gap-3 relative z-10">
                <div className="bg-white p-1 rounded-full shadow-sm ring-1 ring-emerald-200 flex-shrink-0">
                  <img src="/assets/vtu-logo.png" alt="VTU Logo" className="w-8 h-8 object-contain" />
                </div>
                <div className="flex flex-col pt-0.5">
                  <h3 className="text-emerald-800 font-bold font-mono uppercase tracking-[0.12em] text-[13px] mb-1 leading-snug">
                    VTU Approved<br/>Internship Provider
                  </h3>
                  <p className="text-[11px] text-neutral-600 font-medium leading-relaxed">
                    Recognized by Visvesvaraya Technological University (VTU)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <h1 className="font-serif text-[44px] sm:text-6xl md:text-7xl lg:text-[86px] leading-[0.98] tracking-tight text-neutral-950">
                Start your{" "}
                <span className="relative inline-block">
                  <span className="relative z-10">Internship</span>
                  <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 12" preserveAspectRatio="none" aria-hidden>
                    <path d="M2 8 C 80 2, 220 2, 298 8" stroke="#f97316" strokeWidth="4" fill="none" strokeLinecap="round" />
                  </svg>
                </span>{" "}
                Journey with{" "}
                <span className="italic text-indigo-700">Sconti</span>{" "}
                Technologies.
              </h1>
            </Reveal>
            <Reveal delay={150}>
              <p className="mt-8 font-serif italic text-xl md:text-2xl text-neutral-700 max-w-2xl">
                &ldquo;Intern, Innovate, and Inspire — The Sconti Way.&rdquo;
              </p>
            </Reveal>
            <Reveal delay={250}>
              <p className="mt-6 text-base md:text-lg leading-relaxed text-neutral-600 max-w-2xl">
                Build real-world skills through live projects, expert mentorship, and certified internship
                programs at Scontinent Technologies.
              </p>
            </Reveal>
            <Reveal delay={350}>
              <div className="mt-10 flex flex-wrap items-center gap-3">
                <Link to="/apply" className="btn-pill btn-pill--primary" data-testid="hero-get-started">
                  Get Started
                  <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
                </Link>
                <Link
                  to="/internships"
                  data-testid="hero-explore-programs"
                  className="btn-pill btn-pill--secondary"
                >
                  <PlayCircle className="w-4 h-4" strokeWidth={1.5} />
                  Explore Programs
                </Link>
              </div>
            </Reveal>

            {/* Trust row */}
            <Reveal delay={450}>
              <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4 text-xs text-neutral-500">
                <div className="flex items-center gap-3">
                  <div className="flex -space-x-2">
                    {[
                      "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=facearea&facepad=2&w=80&h=80&q=80",
                      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=facearea&facepad=2&w=80&h=80&q=80",
                      "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=facearea&facepad=2&w=80&h=80&q=80",
                      "https://images.unsplash.com/photo-1547425260-76bcadfb4f2c?auto=format&fit=facearea&facepad=2&w=80&h=80&q=80",
                    ].map((src, i) => (
                      <img
                        key={i}
                        src={src}
                        alt=""
                        className="w-8 h-8 rounded-full border-2 border-white object-cover"
                      />
                    ))}
                  </div>
                  <div>
                    <div className="font-serif text-neutral-950 text-base">100,000+ interns</div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500">
                      Building real careers
                    </div>
                  </div>
                </div>
                <div className="hidden md:block h-8 w-px bg-neutral-200" />
                <div className="flex items-center gap-3">
                  <div className="flex text-amber-500 gap-0.5">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                    ))}
                  </div>
                  <div>
                    <div className="font-serif text-neutral-950 text-base">4.9 / 5</div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500">
                      Rated by learners
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={200}>
              <div className="relative">
                {/* Background accent frame */}
                <div className="absolute -inset-4 -z-10 rounded-[32px] bg-gradient-to-br from-orange-100 via-white to-indigo-100 blur-2xl opacity-70" />
                <div className="relative rounded-3xl overflow-hidden border border-neutral-200 aspect-[4/5] bg-neutral-100">
                  <img
                    src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1400&q=80"
                    alt="Students studying and interning together at Scontinent Technologies"
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="eager"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-neutral-950/60 via-transparent to-transparent" />

                  {/* Bottom info card */}
                  <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between gap-3">
                    <div className="text-white">
                      <div className="text-[10px] uppercase tracking-[0.24em] opacity-90">Live cohort</div>
                      <div className="font-serif text-xl mt-1">Now onboarding · Bangalore</div>
                    </div>
                    <div className="grid place-items-center w-11 h-11 rounded-full bg-white text-neutral-950">
                      <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal delay={400}>
          <div className="mt-20 md:mt-28 grid grid-cols-1 sm:grid-cols-3 border-t border-neutral-200">
            {STATS_HERO.map((s, i) => (
              <div key={s.label} className={`py-8 sm:py-10 sm:px-8 border-b sm:border-b-0 ${i !== STATS_HERO.length - 1 ? "sm:border-r" : ""} border-neutral-200`}>
                <div className="font-serif text-4xl md:text-5xl text-neutral-950">{s.value}</div>
                <div className="mt-2 text-sm text-neutral-500">{s.label}</div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- Recognitions ---------------- */
function RecognitionsBar() {
  return (
    <section className="border-t border-neutral-100 py-12 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <p className="text-sm uppercase tracking-[0.24em] text-neutral-800 font-bold text-center mb-8">
          Recognised by
        </p>
        <RecognitionsStrip />
      </div>
    </section>
  );
}

/* ---------------- ABOUT INTRO ---------------- */
function AboutIntro() {
  return (
    <section className="py-24 md:py-32 bg-white border-t border-neutral-100">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionLabel>About Us</SectionLabel>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                Empowering students and professionals through connection & growth
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={150}>
              <p className="text-lg text-neutral-600 leading-relaxed">
                Scontinent Technologies is a leading IT organization providing internships, professional
                training, certifications, and AI-powered products. We build inclusive bridges between
                learning and livelihood, preparing you for the real world.
              </p>
              <Link to="/contact" className="btn-pill btn-pill--secondary mt-8" data-testid="about-cta">
                Get in Touch
                <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
              </Link>
            </Reveal>
            <Reveal delay={250}>
              <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-6">
                {STATS_ABOUT.map((s) => (
                  <div key={s.label} className="border-t border-neutral-200 pt-6">
                    <div className="font-serif text-3xl md:text-4xl text-neutral-950">{s.value}</div>
                    <div className="mt-2 text-xs uppercase tracking-[0.16em] text-neutral-500">{s.label}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- POPULAR INTERNSHIPS ---------------- */
function PopularInternships() {
  return (
    <section className="py-24 md:py-32 bg-[#FAFAFA] border-t border-neutral-100">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <SectionLabel>Popular Internships</SectionLabel>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
              Scontinent Technologies — Internship Programs
            </h2>
            <p className="mt-5 text-neutral-600 leading-relaxed">
              High-quality programs curated for students to gain real-time experience, build in-demand
              skills, and shape their professional careers with expert guidance.
            </p>
          </div>
          <Link to="/internships" className="btn-pill btn-pill--secondary self-start md:self-auto" data-testid="view-all-internships">
            View All Programs
            <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
          </Link>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {INTERNSHIPS.map((it, idx) => (
            <Reveal key={it.slug} delay={idx * 60}>
              <article data-testid={`internship-card-${it.slug}`} className="group bg-white border border-neutral-200 rounded-2xl overflow-hidden flex flex-col h-full hover:border-neutral-900 transition-colors">
                <div className="aspect-[16/10] overflow-hidden bg-neutral-100">
                  <img
                    src={it.image}
                    alt={it.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-1 rounded-full">
                      {it.tag}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.22em] text-neutral-400">
                      Program
                    </span>
                  </div>
                  <h3 className="font-serif text-2xl tracking-tight leading-tight">{it.title}</h3>
                  <div className="mt-4 space-y-1.5 text-xs text-neutral-600">
                    <Row icon={MapPin} label="Location" value={it.location} />
                    <Row icon={Clock} label="Duration" value={it.duration} />
                    <Row icon={Monitor} label="Work Mode" value={it.mode} />
                  </div>
                  <p className="mt-5 text-sm text-neutral-600 leading-relaxed line-clamp-3">
                    {it.short}
                  </p>
                  <div className="mt-6 pt-6 border-t border-neutral-100 flex items-center justify-between">
                    <Link to={`/internships/${it.slug}`} data-testid={`read-more-${it.slug}`} className="text-sm font-medium hover:opacity-70 inline-flex items-center gap-1">
                      Read More <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.75} />
                    </Link>
                    <Link to={`/apply?domain=${encodeURIComponent(it.title)}`} data-testid={`enroll-${it.slug}`} className="btn-pill btn-pill--primary text-xs" style={{ padding: "10px 18px" }}>
                      Enroll Now
                    </Link>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 mt-0.5 text-neutral-400 shrink-0" strokeWidth={1.5} />
      <div>
        <span className="text-neutral-500">{label}: </span>
        <span className="text-neutral-800">{value}</span>
      </div>
    </div>
  );
}

/* ---------------- HOW IT WORKS ---------------- */
function HowItWorks() {
  return (
    <section className="py-24 md:py-32 bg-white border-t border-neutral-100">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl mb-16">
          <SectionLabel>How It Works</SectionLabel>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
            Complete your internship in 4 simple steps
          </h2>
          <p className="mt-5 text-neutral-600 leading-relaxed">
            A clear, structured process from application to certification.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-neutral-200 border border-neutral-200 rounded-2xl overflow-hidden">
          {STEPS.map((s, i) => {
            const numColors = ["text-indigo-100", "text-amber-100", "text-emerald-100", "text-rose-100"];
            const dotColors = ["text-indigo-600", "text-amber-600", "text-emerald-600", "text-rose-600"];
            return (
              <div key={s.step} className="bg-white p-8 md:p-10 relative">
                <div className={`font-serif text-[96px] leading-none absolute -top-3 -left-1 select-none pointer-events-none ${numColors[i]}`}>
                  {s.step}
                </div>
                <div className="relative z-10">
                  <div className={`font-mono text-[10px] uppercase tracking-[0.24em] mb-4 ${dotColors[i]}`}>
                    Step {s.step}
                  </div>
                  <h3 className="font-serif text-2xl tracking-tight">{s.title}</h3>
                  <p className="mt-4 text-sm text-neutral-600 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------- SERVICES GRID ---------------- */
function ServicesGrid() {
  return (
    <section className="py-24 md:py-32 bg-[#FAFAFA] border-t border-neutral-100">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl mb-16">
          <SectionLabel>What We Do</SectionLabel>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
            End-to-end technology solutions for growth
          </h2>
          <p className="mt-5 text-neutral-600 leading-relaxed">
            We engineer bespoke digital solutions that drive enterprise automation, leverage cognitive
            intelligence, and accelerate career capability.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = SERVICE_ICONS[i % SERVICE_ICONS.length];
            const a = SERVICE_ACCENTS[i % SERVICE_ACCENTS.length];
            return (
              <Reveal key={s.title} delay={i * 60}>
                <div className="bg-white border border-neutral-200 rounded-2xl p-8 h-full group hover:border-neutral-900 transition-colors">
                  <div className={`w-12 h-12 rounded-full grid place-items-center mb-6 transition-colors ${a.bg} ${a.text} ${a.border} border ${a.hoverBg} ${a.hoverText} ${a.hoverBorder}`}>
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-2xl tracking-tight">{s.title}</h3>
                  <p className="mt-3 text-sm text-neutral-600 leading-relaxed">{s.intro}</p>
                  <ul className="mt-6 space-y-2 text-sm text-neutral-700">
                    {s.items.map((it) => (
                      <li key={it} className="flex items-start gap-2">
                        <span className={`mt-1.5 inline-block w-1.5 h-1.5 rounded-full ${a.text.replace("text-", "bg-")}`} />
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------- SAM AI HIGHLIGHT ---------------- */
function SamAiHighlight() {
  const features = [
    "AI-Powered Question Solving",
    "Smart Study Notes Generation",
    "Previous Year Questions (PYQ)",
    "Performance Analytics",
    "Personalised Learning Path",
    "NEET, JEE, CET & UPSC Ready",
  ];
  return (
    <section className="py-24 md:py-32 relative overflow-hidden border-t border-neutral-100" style={{ background: "#F5F2ED" }}>
      <div className="noise" />
      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <SectionLabel>Sconti.AI Presents</SectionLabel>
              <div className="mt-4 flex items-center gap-3">
                <span className="font-serif text-6xl md:text-7xl italic tracking-tight text-neutral-950">
                  ∫amAI
                </span>
                <span className="text-xs font-mono uppercase tracking-[0.24em] text-neutral-500">
                  Product
                </span>
              </div>
              <h2 className="mt-6 font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">
                AI-powered exam preparation
              </h2>
              <p className="mt-5 text-neutral-700 leading-relaxed max-w-xl">
                SamAI is an intelligent exam preparation platform designed to assist students in
                mastering competitive exams like NEET, CET, JEE, UPSC, and KPSC with real-time AI
                assistance.
              </p>
            </Reveal>

            <Reveal delay={150}>
              <ul className="mt-10 grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-neutral-800">
                    <Sparkles className="w-3.5 h-3.5 text-neutral-950" strokeWidth={1.5} />
                    {f}
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={250}>
              <div className="mt-10 flex flex-wrap gap-3">
                <a
                  href="https://play.google.com/store/apps/details?id=com.scontinent.sconti&hl=en_IN"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="samai-download"
                  className="btn-pill btn-pill--primary"
                >
                  Download App
                  <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
                </a>
                <Link to="/samai" data-testid="samai-learn-more" className="btn-pill btn-pill--secondary">
                  Learn More
                </Link>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-6">
            <Reveal delay={200}>
              <div className="relative">
                <div className="rounded-3xl bg-white border border-neutral-200 p-6 md:p-8">
                  <img
                    src="/assets/samai-product.png"
                    alt="SamAI — AI-powered exam preparation app"
                    className="w-full h-auto object-contain block"
                    loading="lazy"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-white border border-neutral-200 rounded-2xl p-4 shadow-[0_20px_40px_rgba(0,0,0,0.06)] hidden md:block">
                  <div className="text-xs font-mono uppercase tracking-[0.22em] text-indigo-700">
                    Live AI
                  </div>
                  <div className="mt-1 font-serif text-lg">Now available for NEET</div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- IMPACT STRIP ---------------- */
function ImpactStrip() {
  const icons = [
    { bg: "bg-indigo-50", text: "text-indigo-600", ring: "ring-indigo-100", path: "M12 12a4 4 0 100-8 4 4 0 000 8zM4 20c0-4 4-6 8-6s8 2 8 6" },
    { bg: "bg-orange-50", text: "text-orange-600", ring: "ring-orange-100", path: "M9 3h6l4 4v14H5V3h4zM9 3v4h6V3M8 12h8M8 16h8" },
    { bg: "bg-emerald-50", text: "text-emerald-600", ring: "ring-emerald-100", path: "M4 6h4v14H4zM10 3h4v17h-4zM16 9h4v11h-4z" },
    { bg: "bg-fuchsia-50", text: "text-fuchsia-600", ring: "ring-fuchsia-100", path: "M9 12a3 3 0 100-6 3 3 0 000 6zM15 14a3 3 0 100-6 3 3 0 000 6zM3 21c0-3 3-5 6-5s6 2 6 5M13 21c0-2 2-4 5-4s3 2 3 4" },
  ];
  return (
    <section className="py-24 md:py-32 relative overflow-hidden border-t border-neutral-100" style={{ background: "linear-gradient(180deg, #ffffff 0%, #fff7ed 55%, #fef3c7 100%)" }}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl mb-14">
          <SectionLabel>Our Impact</SectionLabel>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">
            Numbers that speak for themselves.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {STATS_IMPACT.map((s, i) => {
            const ic = icons[i % icons.length];
            return (
              <Reveal key={s.label} delay={i * 80}>
                <div className="flex items-center gap-5 bg-white rounded-full pl-4 pr-8 py-4 border border-neutral-200 shadow-[0_10px_30px_rgba(0,0,0,0.04)]">
                  <div className={`w-14 h-14 rounded-full grid place-items-center ${ic.bg} ${ic.text} ring-4 ${ic.ring}`}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d={ic.path} />
                    </svg>
                  </div>
                  <div>
                    <div className="font-serif text-3xl md:text-4xl text-orange-600 tracking-tight leading-none">
                      {s.value}
                    </div>
                    <div className="mt-1 text-sm text-neutral-600 font-medium">{s.label}</div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------- WHY CHOOSE ---------------- */
function WhyChoose() {
  const icons = [Rocket, GraduationCap, Users, Target];
  return (
    <section className="py-24 md:py-32 bg-white border-t border-neutral-100">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-4">
            <Reveal>
              <SectionLabel>Programs Offered</SectionLabel>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">
                Why Choose Scontinent?
              </h2>
              <p className="mt-6 text-neutral-600 leading-relaxed">
                Kickstart your professional journey with our industry-led internship programs.
              </p>
              <div className="mt-8 space-y-4 text-sm text-neutral-700">
                <MetaRow label="Duration" value="Up to 6 Months" />
                <MetaRow label="Who Can Apply" value="College Students · Recent Graduates" />
                <MetaRow label="Eligibility" value="BE · BCA · BCom · MBA · MCA · BBA · Diploma" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <div className="grid sm:grid-cols-2 gap-6">
              {WHY_US.map((w, i) => {
                const Icon = icons[i % icons.length];
                const accents = [
                  { bg: "bg-indigo-50", text: "text-indigo-700", border: "border-indigo-100" },
                  { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-100" },
                  { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-100" },
                  { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-100" },
                ];
                const a = accents[i % accents.length];
                return (
                  <Reveal key={w.title} delay={i * 80}>
                    <div className="border border-neutral-200 rounded-2xl p-8 h-full hover:border-neutral-900 transition-colors">
                      <div className={`w-11 h-11 rounded-full grid place-items-center ${a.bg} ${a.text} border ${a.border}`}>
                        <Icon className="w-5 h-5" strokeWidth={1.5} />
                      </div>
                      <h3 className="mt-6 font-serif text-2xl tracking-tight">{w.title}</h3>
                      <p className="mt-3 text-sm text-neutral-600 leading-relaxed">{w.desc}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MetaRow({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-4 border-t border-neutral-100 pt-4">
      <span className="text-xs uppercase tracking-[0.18em] text-neutral-500">{label}</span>
      <span className="col-span-2 text-neutral-800">{value}</span>
    </div>
  );
}

/* ---------------- CERTIFICATION ---------------- */
function Certification() {
  return (
    <section className="py-24 md:py-32 bg-[#FAFAFA] border-t border-neutral-100">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionLabel>Certification</SectionLabel>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">
                Scontinent Internship Certification
              </h2>
              <p className="mt-6 text-neutral-600 leading-relaxed">
                Nationally recognised internship certificates for a stronger career foundation. Our
                certifications go beyond recognition — they validate your skills, knowledge, and
                practical experience to employers worldwide.
              </p>
              <Link to="/apply" data-testid="cert-get-cta" className="btn-pill btn-pill--primary mt-8">
                Get Certified
                <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
              </Link>
            </Reveal>
            <Reveal delay={200}>
              <div className="mt-10 relative rounded-2xl overflow-hidden border border-neutral-200 aspect-[4/3]">
                <img
                  src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1400&q=80"
                  alt="Certification"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
            {CERT_BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={i * 80}>
                <div className="bg-white border border-neutral-200 rounded-2xl p-8 h-full">
                  <div className="w-10 h-10 rounded-full bg-neutral-950 text-white grid place-items-center">
                    <Award className="w-4 h-4" strokeWidth={1.75} />
                  </div>
                  <h3 className="mt-6 font-serif text-xl tracking-tight">{b.title}</h3>
                  <p className="mt-3 text-sm text-neutral-600 leading-relaxed">{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- TESTIMONIALS ---------------- */
function Testimonials() {
  const [active, setActive] = useState(0);
  const total = TESTIMONIALS.length;

  useEffect(() => {
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % total);
    }, 4000);
    return () => clearInterval(timer);
  }, [total]);

  const prev = () => setActive((a) => (a - 1 + total) % total);
  const next = () => setActive((a) => (a + 1) % total);

  return (
    <section className="py-24 md:py-32 relative overflow-hidden border-t border-neutral-100" style={{ background: "linear-gradient(180deg, #ffffff 0%, #eef2ff 100%)" }}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl mb-14">
          <SectionLabel>Testimonials</SectionLabel>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">
            What people are saying about us
          </h2>
          <p className="mt-5 text-neutral-600 leading-relaxed">
            Real voices from learners, authors and professionals who have experienced Scontinent
            Technologies first-hand.
          </p>
        </div>

        {/* Carousel */}
        <div className="relative">
          {/* Cards track */}
          <div className="overflow-hidden rounded-2xl">
            <div
              className="flex transition-transform duration-700 ease-in-out"
              style={{ transform: `translateX(-${active * 100}%)` }}
            >
              {TESTIMONIALS.map((t) => (
                <div key={t.name} className="min-w-full px-1">
                  <figure className="bg-white border border-indigo-100 rounded-2xl p-10 md:p-14 flex flex-col shadow-[0_20px_60px_rgba(30,58,138,0.08)]">
                    <div className="font-serif text-6xl leading-none text-indigo-200">&ldquo;</div>
                    <blockquote className="mt-3 text-xl md:text-2xl leading-relaxed text-neutral-800 font-serif italic max-w-3xl">
                      {t.quote}
                    </blockquote>
                    <figcaption className="mt-10 pt-7 border-t border-neutral-100 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-700 to-indigo-900 text-white grid place-items-center font-serif text-lg shrink-0">
                        {t.initials}
                      </div>
                      <div>
                        <div className="font-semibold text-neutral-950">{t.name}</div>
                        <div className="text-sm text-neutral-500 mt-0.5">{t.role}</div>
                      </div>
                    </figcaption>
                  </figure>
                </div>
              ))}
            </div>
          </div>

          {/* Prev / Next buttons */}
          <button
            onClick={prev}
            aria-label="Previous testimonial"
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-5 w-11 h-11 rounded-full bg-white border border-neutral-200 shadow-md flex items-center justify-center text-neutral-600 hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-700 transition-all"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <button
            onClick={next}
            aria-label="Next testimonial"
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-5 w-11 h-11 rounded-full bg-white border border-neutral-200 shadow-md flex items-center justify-center text-neutral-600 hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-700 transition-all"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>

        {/* Dot indicators */}
        <div className="flex justify-center gap-2 mt-8">
          {TESTIMONIALS.map((_, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              aria-label={`Go to testimonial ${i + 1}`}
              className="transition-all duration-300"
              style={{
                width: i === active ? 28 : 8,
                height: 8,
                borderRadius: 99,
                background: i === active ? "#4338ca" : "#c7d2fe",
                border: "none",
                cursor: "pointer",
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- TRUSTED PARTNERS ---------------- */
function TrustedPartners() {
  return (
    <section className="py-16 md:py-20 bg-white border-t border-neutral-100">
      <div className="max-w-7xl mx-auto px-6 md:px-10 mb-6 text-center">
        <SectionLabel>Trusted Partners</SectionLabel>
        <h3 className="mt-3 font-serif text-3xl md:text-4xl tracking-tight">Companies we work with</h3>
        <p className="mt-3 text-neutral-500 text-sm">
          Proud to collaborate with innovative organisations across industries.
        </p>
      </div>
      <PartnersMarquee />
    </section>
  );
}

/* ---------------- FAQ ---------------- */
function FaqSection() {
  const [open, setOpen] = useState(0);
  return (
    <section className="py-24 md:py-32 bg-[#FAFAFA] border-t border-neutral-100">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <SectionLabel>FAQ</SectionLabel>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">
              Common questions
            </h2>
            <p className="mt-5 text-neutral-600 leading-relaxed">
              Get answers to common questions about the Scontinent Internship Program and our other
              offerings.
            </p>
            <div className="mt-8 border border-neutral-200 rounded-2xl p-6 bg-white">
              <div className="font-medium">Still have questions?</div>
              <p className="mt-2 text-sm text-neutral-600">
                Our support team is here to help. Reach us at office@scontinent.com
              </p>
              <a href="mailto:office@scontinent.com" className="btn-pill btn-pill--secondary mt-6" data-testid="faq-contact-us">
                Contact Us
              </a>
            </div>
          </div>
          <div className="lg:col-span-8">
            <div className="border border-neutral-200 rounded-2xl overflow-hidden bg-white">
              {FAQ.map((item, i) => (
                <div key={item.q} className={`${i !== 0 ? "border-t border-neutral-100" : ""}`}>
                  <button
                    data-testid={`faq-toggle-${i}`}
                    onClick={() => setOpen(open === i ? -1 : i)}
                    className="w-full text-left px-6 md:px-8 py-6 flex items-center justify-between gap-4 hover:bg-neutral-50 transition-colors"
                  >
                    <span className="font-serif text-lg md:text-xl tracking-tight">{item.q}</span>
                    <span className={`grid place-items-center w-8 h-8 rounded-full border border-neutral-200 transition-transform ${open === i ? "rotate-45" : ""}`}>
                      <span className="block w-3 h-px bg-neutral-950" />
                      <span className="block h-3 w-px bg-neutral-950 -mt-[7px]" />
                    </span>
                  </button>
                  {open === i && (
                    <div className="px-6 md:px-8 pb-6 text-neutral-600 leading-relaxed">
                      {item.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- APPLY SECTION ---------------- */
function ApplySection() {
  return (
    <section id="internship-form" className="py-24 md:py-32 bg-white border-t border-neutral-100">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <SectionLabel>Internship Program</SectionLabel>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">
              Launch your career with industry experience
            </h2>
            <p className="mt-5 text-neutral-600 leading-relaxed">
              Gain practical experience through live industry projects, expert mentorship, and
              career-focused internship programs.
            </p>
            <ul className="mt-10 space-y-6">
              {[
                { t: "Live Projects", d: "Real-world development experience." },
                { t: "Expert Mentorship", d: "Guidance from experienced engineers." },
                { t: "Certification", d: "Verified completion certificate." },
              ].map((b) => (
                <li key={b.t} className="flex gap-4">
                  <div className="w-9 h-9 rounded-full bg-neutral-950 text-white grid place-items-center shrink-0">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  <div>
                    <div className="font-serif text-xl tracking-tight">{b.t}</div>
                    <div className="text-sm text-neutral-600 mt-1">{b.d}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7">
            <ApplicationForm />
          </div>
        </div>
      </div>
    </section>
  );
}

function SectionLabel({ children }) {
  return (
    <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.24em] text-neutral-500">
      <span className="inline-block w-6 h-px bg-neutral-400" />
      {children}
    </span>
  );
}
